import React from "react";
import { formatNumber } from "@utils/formatUtils";

const INVESTOR_TYPES = [
  {
    id: 0,
    title: "Nhà đầu tư mới",
    desc: "< 2 năm · Đầu tư vài lần/tháng",
  },
  {
    id: 1,
    title: "Nhà đầu tư chủ động",
    desc: "2-5 năm · Đầu tư vài lần/tuần",
  },
  {
    id: 2,
    title: "Trader chuyên nghiệp",
    desc: "5+ năm · Đầu tư mỗi ngày",
  },
];

const CAPITAL_SIZES = [
  { id: "50tr", label: "50 triệu", value: 50_000_000 },
  { id: "100tr", label: "100 triệu", value: 100_000_000 },
  { id: "500tr", label: "500 triệu", value: 500_000_000 },
  { id: "1ty", label: "1 tỷ", value: 1_000_000_000 },
];

const OnboardingQuestionnaire = ({
  answers,
  onAnswer,
  onFinalNext,
  onSkip,
}) => {
  const [capitalInput, setCapitalInput] = React.useState("");
  const capitalInputRef = React.useRef(null);
  const cursorPosRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (cursorPosRef.current !== null && capitalInputRef.current) {
      capitalInputRef.current.setSelectionRange(
        cursorPosRef.current,
        cursorPosRef.current,
      );
      cursorPosRef.current = null;
    }
  }, [capitalInput]);

  const handleCapitalChange = (e) => {
    const { value, selectionStart } = e.target;
    const raw = value.replace(/,/g, "");
    if (raw !== "" && !/^\d+$/.test(raw)) return;

    const formatted = raw === "" ? "" : formatNumber(parseInt(raw, 10));
    const digitsBefore = value.slice(0, selectionStart).replace(/,/g, "").length;

    let cursor = 0;
    for (let seen = 0; cursor < formatted.length && seen < digitsBefore; cursor++) {
      if (formatted[cursor] !== ",") seen++;
    }

    cursorPosRef.current = cursor;
    setCapitalInput(formatted);
    onAnswer("capitalAmount", raw);
  };

  const isComplete =
    answers.investingExperience != null && answers.capitalAmount;

  return (
    <div className="flex flex-col items-center w-full max-w-[462px]">
      <div className="mb-[24px] text-center">
        <h1 className="text-[24px] leading-[32px] font-bold text-[#111827] mb-[4px]">
          Thiết lập danh mục của riêng bạn
        </h1>
        <p className="text-[16px] leading-[24px] font-[400] text-[#636369] whitespace-nowrap">
          Trả lời 2 câu hỏi để Nebula cá nhân hóa trải nghiệm đầu tư cho bạn
        </p>
      </div>

      <div className="w-full bg-white rounded-[16px] border border-[#ececed] pt-[20px] px-[20px] pb-[24px] shadow-[0_0_20px_0_#0000000D]">
        <div className="space-y-[16px]">
          <div>
            <p className="text-[14px] font-[500] leading-[20px] text-[#636369] mb-[16px]">
              Bạn là...?
            </p>
            <div className="space-y-[16px]">
              {INVESTOR_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => onAnswer("investingExperience", type.id)}
                  className={`w-full flex flex-col items-center justify-center h-[56px] p-[8px] rounded-[12px] border transition-all text-center group ${
                    answers.investingExperience === type.id
                      ? "border-[#C5C5C7] bg-white"
                      : "border-[#ececed] bg-white hover:border-[#C5C5C7]"
                  }`}
                >
                  <h3 className="text-[16px] font-bold text-[#111827] leading-[24px]">
                    {type.title}
                  </h3>
                  <p className="text-[10px] text-[#636369] leading-[16px]">
                    {type.desc}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[14px] font-[500] leading-[20px] text-[#636369] mb-[16px]">
              Quy mô vốn của bạn?
            </p>
            <div className="flex items-center border border-[#c5c5c7] rounded-[12px] px-[16px] py-[12px] bg-white">
              <input
                ref={capitalInputRef}
                type="text"
                inputMode="numeric"
                value={capitalInput}
                onChange={handleCapitalChange}
                placeholder="Nhập số vốn của bạn"
                className="flex-1 text-[16px] text-[#111827] placeholder-[#a0a0a5] outline-none bg-transparent"
              />
              <span className="text-[16px] text-[#a0a0a5] ml-[8px] shrink-0">
                VNĐ
              </span>
            </div>
            <div className="flex flex-wrap gap-[8px] mt-[12px]">
              {CAPITAL_SIZES.map((size) => (
                <button
                  key={size.id}
                  onClick={() => {
                    setCapitalInput(formatNumber(size.value));
                    onAnswer("capitalAmount", size.id);
                  }}
                  className={`h-[32px] leading-[20px] px-[8px] rounded-[16px] text-[14px] font-medium transition-all ${
                    answers.capitalAmount === size.id
                      ? "bg-[#3c3c43] text-white"
                      : "bg-[#f5f5f5] text-[#636369]"
                  }`}
                >
                  {size.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[24px] w-full flex flex-col items-center gap-[16px]">
        <button
          disabled={!isComplete}
          onClick={onFinalNext}
          className={`w-[224px] h-[48px] rounded-[12px] text-[16px] font-medium transition-all ${
            isComplete
              ? "bg-[#3c3c43] text-white hover:opacity-90 active:opacity-80"
              : "bg-[#c5c5c7] text-white cursor-not-allowed"
          }`}
        >
          Tạo danh mục ngay
        </button>
        <button
          onClick={onSkip}
          className="text-[14px] font-medium text-[#636369] hover:text-[#111827] transition-colors"
        >
          Tôi không cần tạo danh mục
        </button>
      </div>
    </div>
  );
};

export default OnboardingQuestionnaire;
